import React, { useState, useEffect } from 'react';
import { policiesApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  FileText, Plus, PencilSimple, Trash, ShieldCheck, Eye, Download,
  Lock, Users, Warning, Clock, CheckCircle, Notebook,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const CATEGORY_CONFIG = {
  privacy: { label: 'Privacy', color: 'bg-violet-100 text-violet-700', icon: Lock },
  security: { label: 'Security', color: 'bg-emerald-100 text-emerald-700', icon: ShieldCheck },
  access: { label: 'Access Control', color: 'bg-sky-100 text-sky-700', icon: Users },
  incident: { label: 'Incident Response', color: 'bg-red-100 text-red-700', icon: Warning },
  retention: { label: 'Data Retention', color: 'bg-amber-100 text-amber-700', icon: Clock },
  general: { label: 'General', color: 'bg-slate-100 text-slate-700', icon: FileText },
};

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700' },
  draft: { label: 'Draft', color: 'bg-amber-100 text-amber-700' },
  archived: { label: 'Archived', color: 'bg-slate-100 text-slate-500' },
};

const PoliciesPage = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [editDialog, setEditDialog] = useState(null);
  const [createDialog, setCreateDialog] = useState(false);
  const [form, setForm] = useState({ title: '', slug: '', category: 'general', content: '', version: '1.0', status: 'draft' });
  const [seeding, setSeeding] = useState(false);

  useEffect(() => { loadPolicies(); }, []);

  const loadPolicies = async () => {
    try {
      const res = await policiesApi.list();
      setPolicies(res.data);
      if (res.data.length > 0 && !selectedPolicy) setSelectedPolicy(res.data[0]);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const seedDefaults = async () => {
    setSeeding(true);
    try {
      const res = await policiesApi.seedDefaults();
      toast.success(res.data.message);
      loadPolicies();
    } catch (e) { toast.error('Failed to seed policies'); }
    finally { setSeeding(false); }
  };

  const handleCreate = async () => {
    if (!form.title) { toast.error('Title required'); return; }
    try {
      await policiesApi.create({ ...form, slug: form.slug || form.title.toLowerCase().replace(/\s+/g, '-') });
      toast.success('Policy created');
      setCreateDialog(false);
      setForm({ title: '', slug: '', category: 'general', content: '', version: '1.0', status: 'draft' });
      loadPolicies();
    } catch (e) { toast.error('Failed to create'); }
  };

  const handleUpdate = async () => {
    if (!editDialog) return;
    try {
      await policiesApi.update(editDialog.id, form);
      toast.success('Policy updated');
      setEditDialog(null);
      loadPolicies();
    } catch (e) { toast.error('Failed to update'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this policy?')) return;
    try {
      await policiesApi.remove(id);
      toast.success('Policy deleted');
      if (selectedPolicy?.id === id) setSelectedPolicy(null);
      loadPolicies();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const renderMarkdown = (content) => {
    // Simple markdown renderer
    return content
      .split('\n')
      .map((line, i) => {
        if (line.startsWith('## ')) return <h2 key={i} className="text-lg font-bold text-slate-800 mt-4 mb-2">{line.slice(3)}</h2>;
        if (line.startsWith('### ')) return <h3 key={i} className="text-base font-semibold text-slate-700 mt-3 mb-1">{line.slice(4)}</h3>;
        if (line.startsWith('| ')) {
          const cells = line.split('|').filter(Boolean).map(c => c.trim());
          return <div key={i} className="grid grid-cols-2 gap-2 text-xs border-b border-slate-100 py-1">{cells.map((c, j) => <span key={j} className={j === 0 ? 'font-medium text-slate-700' : 'text-slate-500'}>{c}</span>)}</div>;
        }
        if (line.startsWith('- ')) return <li key={i} className="text-sm text-slate-600 ml-4 list-disc">{line.slice(2)}</li>;
        if (line.startsWith('1. ') || line.startsWith('2. ') || line.startsWith('3. ') || line.startsWith('4. ') || line.startsWith('5. ') || line.startsWith('6. ')) return <li key={i} className="text-sm text-slate-600 ml-4 list-decimal">{line.slice(3)}</li>;
        if (line.trim() === '') return <br key={i} />;
        if (line.startsWith('|---')) return null;
        return <p key={i} className="text-sm text-slate-600 leading-relaxed">{line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/<strong>(.*?)<\/strong>/g, (_, t) => t)}</p>;
      });
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" /></div>;

  return (
    <div className="space-y-5" data-testid="policies-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="bg-gradient-to-r from-violet-900 via-slate-900 to-slate-800 rounded-2xl p-6 text-white" data-testid="policies-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-violet-500/20 flex items-center justify-center border border-violet-500/30">
              <Notebook className="h-6 w-6 text-violet-400" weight="duotone" />
            </div>
            <div>
              <h1 className="text-xl font-bold font-['Outfit']" data-testid="policies-heading">HIPAA Policy Documentation</h1>
              <p className="text-sm text-slate-300 mt-0.5">Manage compliance policies, security standards & procedures</p>
            </div>
          </div>
          <div className="flex gap-2">
            {policies.length === 0 && (
              <Button size="sm" onClick={seedDefaults} disabled={seeding}
                className="h-8 bg-violet-600 hover:bg-violet-700 text-white text-xs gap-1.5" data-testid="seed-policies-btn">
                <ShieldCheck className="h-3.5 w-3.5" /> {seeding ? 'Creating...' : 'Generate Default Policies'}
              </Button>
            )}
            <Button size="sm" onClick={() => { setCreateDialog(true); setForm({ title: '', slug: '', category: 'general', content: '', version: '1.0', status: 'draft' }); }}
              className="h-8 bg-white/10 hover:bg-white/20 text-white text-xs gap-1.5 border border-white/20" data-testid="create-policy-btn">
              <Plus className="h-3.5 w-3.5" /> New Policy
            </Button>
          </div>
        </div>
      </div>

      {policies.length === 0 ? (
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardContent className="py-16 text-center">
            <Notebook className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700">No Policies Yet</h3>
            <p className="text-sm text-slate-500 mt-1 mb-4">Click "Generate Default Policies" to create standard HIPAA compliance documents</p>
            <Button onClick={seedDefaults} disabled={seeding} className="bg-violet-600 hover:bg-violet-700 text-white gap-2" data-testid="seed-empty-btn">
              <ShieldCheck className="h-4 w-4" /> {seeding ? 'Creating...' : 'Generate Default HIPAA Policies'}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Policy List */}
          <div className="space-y-2" data-testid="policy-list">
            {policies.map(p => {
              const cat = CATEGORY_CONFIG[p.category] || CATEGORY_CONFIG.general;
              const st = STATUS_CONFIG[p.status] || STATUS_CONFIG.draft;
              const Icon = cat.icon;
              const isSelected = selectedPolicy?.id === p.id;
              return (
                <div key={p.id} onClick={() => setSelectedPolicy(p)}
                  className={`p-3 rounded-xl border cursor-pointer transition-all ${isSelected ? 'border-violet-300 bg-violet-50 shadow-sm' : 'border-slate-200 bg-white hover:border-violet-200 hover:shadow-sm'}`}
                  data-testid={`policy-item-${p.slug}`}>
                  <div className="flex items-start gap-3">
                    <div className={`h-8 w-8 rounded-lg ${cat.color} flex items-center justify-center shrink-0`}>
                      <Icon className="h-4 w-4" weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{p.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={`${cat.color} border-0 text-[9px]`}>{cat.label}</Badge>
                        <Badge className={`${st.color} border-0 text-[9px]`}>{st.label}</Badge>
                        <span className="text-[9px] text-slate-400">v{p.version}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Policy Content */}
          <div className="lg:col-span-2">
            {selectedPolicy ? (
              <Card className="bg-white border border-slate-200 shadow-sm">
                <CardHeader className="pb-3 border-b border-slate-100">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg font-bold font-['Outfit'] text-slate-800">{selectedPolicy.title}</CardTitle>
                      <div className="flex items-center gap-2 mt-1.5">
                        <Badge className={`${(CATEGORY_CONFIG[selectedPolicy.category] || CATEGORY_CONFIG.general).color} border-0 text-[10px]`}>
                          {(CATEGORY_CONFIG[selectedPolicy.category] || CATEGORY_CONFIG.general).label}
                        </Badge>
                        <Badge className={`${(STATUS_CONFIG[selectedPolicy.status] || STATUS_CONFIG.draft).color} border-0 text-[10px]`}>
                          {(STATUS_CONFIG[selectedPolicy.status] || STATUS_CONFIG.draft).label}
                        </Badge>
                        <span className="text-[10px] text-slate-400">Version {selectedPolicy.version}</span>
                        {selectedPolicy.updated_at && <span className="text-[10px] text-slate-400">Updated: {new Date(selectedPolicy.updated_at).toLocaleDateString('en-IN')}</span>}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-sky-600 hover:bg-sky-50"
                        onClick={() => { setEditDialog(selectedPolicy); setForm({ title: selectedPolicy.title, slug: selectedPolicy.slug, category: selectedPolicy.category, content: selectedPolicy.content, version: selectedPolicy.version, status: selectedPolicy.status }); }}
                        data-testid="edit-policy-btn"><PencilSimple className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50"
                        onClick={() => handleDelete(selectedPolicy.id)} data-testid="delete-policy-btn"><Trash className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4 max-h-[600px] overflow-y-auto">
                  <div className="prose prose-sm max-w-none">
                    {renderMarkdown(selectedPolicy.content || '')}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-white border border-slate-200 shadow-sm">
                <CardContent className="py-16 text-center">
                  <Eye className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-sm text-slate-500">Select a policy to view its content</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={createDialog || !!editDialog} onOpenChange={() => { setCreateDialog(false); setEditDialog(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">{editDialog ? 'Edit Policy' : 'Create New Policy'}</DialogTitle>
            <DialogDescription>{editDialog ? 'Update the policy document' : 'Add a new HIPAA compliance policy'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="Policy title" data-testid="policy-title-input" /></div>
              <div><Label>Version</Label><Input value={form.version} onChange={e => setForm(f => ({...f, version: e.target.value}))} placeholder="1.0" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Category</Label>
                <select value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm">
                  {Object.entries(CATEGORY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div><Label>Status</Label>
                <select value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))} className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm">
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>
            <div><Label>Content (Markdown supported)</Label>
              <Textarea value={form.content} onChange={e => setForm(f => ({...f, content: e.target.value}))}
                placeholder="## Policy Title&#10;&#10;### Section 1&#10;Policy content..." className="min-h-[300px] font-mono text-xs" data-testid="policy-content-input" />
            </div>
            <Button className="w-full bg-gradient-to-r from-violet-500 to-purple-600 text-white"
              onClick={editDialog ? handleUpdate : handleCreate} data-testid="save-policy-btn">
              {editDialog ? 'Update Policy' : 'Create Policy'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PoliciesPage;
